import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ResolvedRating } from 'src/ratings/useRiskRatingResolver';

import type { TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';

// Flat, denormalised row — mirrors the other "by Risks" relational sources
// (RiskActionRow, RiskControlRow). Carries only the risk attributes the source
// charts/filters on plus the per-category impact bands; it does NOT extend
// RiskRegisterFields.
export type RiskImpactRatingRow = TableRecord & {
  Id: string;
  RiskId: string;
  RiskFriendlyId: string;
  RiskTitle: string;
  RiskTier: string;
  RiskStatus: string;
  RiskTreatment: string;
  ImpactCategory: string;
  ResidualImpactRatingLabel: string;
  ResidualImpactRatingColour: string | null;
  ResidualImpactRatingValue: number | null;
  InherentImpactRatingLabel: string;
  InherentImpactRatingColour: string | null;
  InherentImpactRatingValue: number | null;
};

type ResolveImpact = (
  value: number | null | undefined
) => ResolvedRating | undefined;

// The minimal slice of a labelled risk-register row this transform reads. It's
// declared here rather than depending on the full RiskRegisterFields so the
// pure transform owns its input contract and tests can build fixtures without
// casting. Production RiskRegisterFields satisfies this structurally.
export type RiskForImpactRating = {
  Id: string;
  SequentialIdLabel?: string | null;
  Title: string;
  TierLabelled: string;
  StatusLabelled?: string | null;
  TreatmentLabelled?: string | null;
  assessmentResults?:
    | {
        riskAssessmentResult?: {
          ControlType: Risk_Assessment_Result_Control_Type_Enum;
          impacts: { Label: string; Value: number | null }[];
        } | null;
      }[]
    | null;
};

/**
 * Flattens the risk register list into one row per (risk × impact category).
 *
 * Residual impact bands are resolved from the latest CONTROLLED assessment
 * result; inherent impact bands from the latest UNCONTROLLED one. Band
 * resolution (scoring-settings vs taxonomy) is delegated to the injected
 * `resolveImpact` so this transform stays pure and resolution-agnostic.
 */
export const flattenRisksToImpactRatingRows = (
  risks: RiskForImpactRating[],
  resolveImpact: ResolveImpact
): RiskImpactRatingRow[] =>
  risks.flatMap((risk) => {
    // assessmentResults are ordered latest-first (TestDate desc_nulls_last,
    // then CreatedAtTimestamp desc_nulls_last), so `.find` picks the latest of
    // each control type — mirrors getAssessmentResultCustomAttributeData.
    const latestControlled = risk.assessmentResults?.find(
      (ar) =>
        ar.riskAssessmentResult?.ControlType ===
        Risk_Assessment_Result_Control_Type_Enum.Controlled
    )?.riskAssessmentResult;

    const latestUncontrolled = risk.assessmentResults?.find(
      (ar) =>
        ar.riskAssessmentResult?.ControlType ===
        Risk_Assessment_Result_Control_Type_Enum.Uncontrolled
    )?.riskAssessmentResult;

    const controlledImpacts = latestControlled?.impacts ?? [];
    const uncontrolledImpacts = latestUncontrolled?.impacts ?? [];

    // Ordered union of category labels: controlled first, then any
    // uncontrolled-only categories, deduped by Label preserving first-seen order.
    const categoryLabels: string[] = [];
    const seen = new Set<string>();
    for (const impact of [...controlledImpacts, ...uncontrolledImpacts]) {
      if (!seen.has(impact.Label)) {
        seen.add(impact.Label);
        categoryLabels.push(impact.Label);
      }
    }

    return categoryLabels.map((label): RiskImpactRatingRow => {
      const residualValue =
        controlledImpacts.find((i) => i.Label === label)?.Value ?? null;
      const inherentValue =
        uncontrolledImpacts.find((i) => i.Label === label)?.Value ?? null;

      const residual = resolveImpact(residualValue);
      const inherent = resolveImpact(inherentValue);

      return {
        Id: `${risk.Id}::${label}`,
        RiskId: risk.Id,
        RiskFriendlyId: risk.SequentialIdLabel ?? '',
        RiskTitle: risk.Title,
        RiskTier: risk.TierLabelled,
        RiskStatus: risk.StatusLabelled ?? '',
        RiskTreatment: risk.TreatmentLabelled ?? '',
        ImpactCategory: label,
        ResidualImpactRatingLabel: residual?.label ?? UNRATED,
        ResidualImpactRatingColour: residual?.color ?? null,
        ResidualImpactRatingValue: residualValue,
        InherentImpactRatingLabel: inherent?.label ?? UNRATED,
        InherentImpactRatingColour: inherent?.color ?? null,
        InherentImpactRatingValue: inherentValue,
      };
    });
  });
